import { useEffect, type FC } from 'react'
import { observer } from 'mobx-react-lite'
import { Button, ContainerActions, Dropdown } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { FeatureRestrictedModalStore } from 'entities/UsersPermissions'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { contactsDetailsGlobalStore } from 'widgets/ContactsDetails/store'
import { type ContactsTableStore } from 'pages/contacts/ui/ContactsTable'
import { ContactsFiltersHeader } from 'pages/contacts/ui/ContactsMenu/ui/ContactsFiltersHeader/ContactsFiltersHeader'
import { type ImportContactsStore } from 'pages/contacts/ui/ImportContacts/store/ImportContactsStore'
import { ContactsPath } from 'pages/contacts/route/contactsPath'
import { type ContactsMenuStore } from 'pages/contacts/ui/ContactsMenu'
import styles from './styles.module.scss'

type IContactsContainerActionsProps = {
  contactsTableStore: ContactsTableStore
  contactsMenuStore: ContactsMenuStore
  importContactsStore: ImportContactsStore
}

export const ContactsContainerActions: FC<IContactsContainerActionsProps> = observer(
  ({ contactsTableStore, importContactsStore, contactsMenuStore }) => {
    const { inboxesStore, usersStore, userPermissionsStore } = useEntitiesContext()
    const { addNewContactManually } = contactsTableStore
    const { checkIsImportDisabled, contactsImportModuleStore } = importContactsStore
    const { totalHistory } = contactsImportModuleStore
    const { handleChangeCollapse } = contactsDetailsGlobalStore

    const createDisabled = inboxesStore.isUnifiedInboxOnly
    const importAvailable = !usersStore.user?.isMemberRole && !usersStore.user?.isViewOnlyRole

    const onImport = () => {
      userPermissionsStore.getItem('can_create_contacts')
        ? importContactsStore.openModal()
        : new FeatureRestrictedModalStore('Can create Contacts')
    }

    const checkImportDisabling = () => {
      return checkIsImportDisabled(totalHistory)
    }

    const addNewContact = () => {
      addNewContactManually()
      handleChangeCollapse(false)
    }

    const onAddNewContact = () => {
      userPermissionsStore.getItem('can_create_contacts')
        ? addNewContact()
        : new FeatureRestrictedModalStore('Can create Contacts')
    }

    useEffect(() => {
      return () => {
        contactsTableStore.resetSearch()
      }
    }, [])

    return (
      <div className={styles.wrapper}>
        <ContainerActions
          padding='0'
          leftActions={[
            {
              iconButtonComponent: contactsMenuStore ? (
                <ContactsFiltersHeader contactsMenuStore={contactsMenuStore} />
              ) : null,
            },
          ]}
          rightActions={[
            {
              iconButtonComponent: (
                <div className={styles.leftActions}>
                  {importAvailable && (
                    <Button
                      text='Import'
                      icon='upload'
                      disabled={checkImportDisabling()}
                      contained='tertiary'
                      onClick={onImport}
                    />
                  )}
                  <Button
                    text='Create'
                    disabled={createDisabled}
                    icon='add'
                    contained='tertiary'
                    onClick={onAddNewContact}
                  />
                  <Dropdown
                    ariaLabel={'ContactsContainerActions'}
                    width={165}
                    icon={'moreHorizontal'}
                    placement={'bottom-start'}
                    tooltipProps={{
                      label: 'Show actions',
                      placement: 'top',
                      disableInteractive: true,
                    }}
                    iconButtonProps={{
                      size: 'medium',
                      ariaLabel: 'ContactsContainerActions_trigger',
                    }}
                    items={[
                      {
                        id: 1,
                        label: 'Import history',
                        iconL: 'clockRecent',
                        ariaLabel: 'DropdownList_ImportHistory',
                        onClick: () => {
                          userPermissionsStore.getItem('can_create_contacts')
                            ? uiStore.changeRoute({
                                path: ContactsPath.history,
                              })
                            : new FeatureRestrictedModalStore('Can create Contacts')
                        },
                      },
                      {
                        id: 2,
                        label: 'Contact cleanup',
                        iconL: 'arrowLinesHorizontal',
                        ariaLabel: 'DropdownList_ContactCleanup',
                        onClick: () => {
                          uiStore.changeRoute({
                            path: ContactsPath.cleanup,
                          })
                        },
                      },
                    ]}
                  />
                </div>
              ),
            },
          ]}
        />
      </div>
    )
  }
)
